import { getFormProps, getInputProps, useForm } from "@conform-to/react";
import { parseWithZod } from "@conform-to/zod";
import { useEffect, useRef, useState } from "react";
import { data, href, redirect, useActionData, useLoaderData } from "react-router";
import { HoneypotInputs } from "remix-utils/honeypot/react";
import { z } from "zod";

import { ErrorList } from "@/components/forms";
import { ViewToggle } from "@/components/view-toggle.tsx";
import { MAX_ALLOCATION_WEIGHT } from "@/constants/index.ts";
import { FUNCTIONS } from "@/constants/budget-functions.ts";
import { PUBLIC_DOMAIN_SCHEME, type ViewSchemeId } from "@/constants/grouping-schemes.ts";
import { Icon } from "@/ui/icon";
import { ConformSlider } from "@/ui/conform-slider.tsx";
import { useScrollProgress } from "@/hooks/use-scroll-progress";
import { useElementHeightVar } from "@/hooks/use-element-height-var.ts";
import { checkHoneypot } from "@/utils/honeypot.server";
import { bpsToSliderWeight, sum, toStoredAllocations } from "@/utils/normalize-weights.ts";
import {
  getOrCreateParticipantSession,
  getParticipantBySession,
} from "@/utils/participant-session.server.ts";

import { type Route } from "./+types/priorities";
import { getFunctionDetailsById } from "@/utils/budget-data.ts";
import { AllocationService } from "@/services/allocation-service.server.ts";
import { ParticipantService } from "@/services/participant-service.server.ts";
import type { FinalAllocationItem } from "@/services/participant-service.server.ts";
import { Card, CardContent, CardTitle } from "#app/components/ui/card.tsx";
import { Progress } from "#app/components/ui/progress.tsx";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "#app/components/ui/collapsible.tsx";
import {
  TypographyH1,
  TypographyLead,
  TypographyH2,
  TypographyP,
} from "#app/components/ui/typography.tsx";
import { Button } from "#app/components/ui/button.tsx";
import { Separator } from "#app/components/ui/separator.tsx";
import { cn } from "#app/utils/misc.tsx";
import { Info } from "lucide-react";
import BackToTop from "#app/components/back-to-top.tsx";

const formSchema = z
  .object({
    allocations: z.array(
      z.object({
        id: z.string(),
        weight: z.coerce.number().int().min(0).max(MAX_ALLOCATION_WEIGHT),
      }),
    ),
  })
  .refine((value) => value.allocations.some((allocation) => allocation.weight > 0), {
    message: "Give weight to at least one category to proceed.",
  });

export type AllocationFormInput = z.infer<typeof formSchema>;

export async function loader({ request }: Route.LoaderArgs) {
  const participant = await getParticipantBySession(request);
  const existingAllocation = participant
    ? await AllocationService.getAllocationByParticipantId(participant.id)
    : null;

  return data({ existingAllocation, existingParticipant: !!participant });
}

export async function action({ request }: Route.ActionArgs) {
  const isFirstTime = (await getParticipantBySession(request)) === null;
  const { headers, participantId } = await getOrCreateParticipantSession(request);
  const formData = await request.formData();
  await checkHoneypot(formData);

  const submission = parseWithZod(formData, { schema: formSchema });

  if (submission.status !== "success") {
    return data(
      {
        resultType: "error",
        result: submission.reply({
          formErrors: ["There was a problem processing your allocations."],
        }),
      },
      { headers, status: 400 },
    );
  }

  // Untouched (weight 0) categories are dropped here — only categories that
  // received weight are persisted. The non-zero items still sum to 10,000.
  const finalAllocation: FinalAllocationItem[] = toStoredAllocations(submission.value.allocations);

  // Ensure the basis points sum to 10,000
  if (sum(finalAllocation.map((a) => a.bps)) !== 10000) {
    return data(
      {
        resultType: "error",
        result: submission.reply({
          formErrors: ["Unable to normalize allocations."],
        }),
      },
      { headers, status: 400 },
    );
  }

  await ParticipantService.saveParticipantAllocations({
    participantId,
    allocations: finalAllocation,
  });

  return redirect(isFirstTime ? href("/first-look") : href("/comparison"), { headers });
}

export default function PrioritiesRoute() {
  const actionData = useActionData<typeof action>();
  const { existingAllocation, existingParticipant } = useLoaderData<typeof loader>();
  const allocatableCategories = FUNCTIONS.filter((f) => f.allocatable !== false);
  const [viewScheme, setViewScheme] = useState<ViewSchemeId>("public_domain");

  const contentRef = useRef<HTMLElement>(null);
  const { sentinelRef, progress } = useScrollProgress(contentRef);
  const toolbarRef = useElementHeightVar<HTMLDivElement>("--priorities-toolbar-height");

  const existingAllocationByCategoryId = new Map(
    existingAllocation?.items.map((item) => [item.categoryCode, item.weightBps]) ?? [],
  );

  const [sliderWeights, setSliderWeights] = useState<Record<string, number>>(() =>
    Object.fromEntries(
      allocatableCategories.map((c) => {
        const existingBps = existingAllocationByCategoryId.get(c.id);
        const weight =
          existingBps === undefined ? 0 : bpsToSliderWeight(existingBps, MAX_ALLOCATION_WEIGHT);
        return [c.id, weight];
      }),
    ),
  );

  const [form, fields] = useForm<AllocationFormInput>({
    defaultValue: {
      allocations: allocatableCategories.map((c) => {
        const existingBps = existingAllocationByCategoryId.get(c.id);

        if (existingBps === undefined) {
          return { id: c.id, weight: 0 };
        }

        return {
          id: c.id,
          weight: bpsToSliderWeight(existingBps, MAX_ALLOCATION_WEIGHT),
        };
      }),
    },
    lastResult: actionData?.result,
    shouldValidate: "onSubmit",
    shouldRevalidate: "onInput",
    onValidate: ({ formData }) => {
      return parseWithZod(formData, { schema: formSchema });
    },
  });
  const allocations = fields.allocations.getFieldList();

  // Form-level errors (e.g. the all-zero guard) render above the allocations.
  // Conform focuses the first field on a failed submit, scrolling the user to
  // the top, so bring the banner into view to make sure it isn't missed.
  const formErrorRef = useRef<HTMLDivElement>(null);
  const formErrorText = form.errors?.join(" ") ?? "";
  useEffect(() => {
    if (formErrorText) {
      formErrorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [formErrorText]);

  const allocationsByFunctionId = new Map(
    allocations.flatMap((a, i) => {
      const fid = a.getFieldset().id.initialValue;
      return fid ? [[fid, { field: a, globalIndex: i }] as const] : [];
    }),
  );

  const functionTrackColorVar = new Map<string, string>(
    PUBLIC_DOMAIN_SCHEME.groups.flatMap((group) =>
      group.functionIds.map((fid) => [fid, group.color.replace(/^text-/, "--")] as const),
    ),
  );

  const renderAllocationItem = (fnId: string, field?: (typeof allocations)[number]) => {
    const fnData = getFunctionDetailsById(fnId);
    if (!fnData) return null;
    const trackColorVar = functionTrackColorVar.get(fnId);
    const categoryField = field?.getFieldset();
    const isNetInterest = fnId === "net_interest";

    return (
      <article key={fnId}>
        <Collapsible className="flex flex-col gap-2">
          <h3>
            <CollapsibleTrigger className="flex items-center gap-1.5 text-left text-ink-2 data-panel-open:text-you underline decoration-dotted decoration-you underline-offset-2 hover:cursor-pointer">
              {isNetInterest && (
                <Icon name="lock-closed" className="size-3.5 shrink-0 text-ink-faint" />
              )}
              {fnData.name}
            </CollapsibleTrigger>
          </h3>
          {categoryField && (
            <div className="flex flex-col mx-2">
              <ConformSlider
                meta={categoryField.weight}
                value={sliderWeights[fnId] ?? 0}
                onValueChange={(v) => setSliderWeights((prev) => ({ ...prev, [fnId]: v }))}
                min={0}
                max={MAX_ALLOCATION_WEIGHT}
                step={1}
                ariaLabel="Category weight"
                trackColorVar={trackColorVar}
              />
              <input {...getInputProps(categoryField.id, { type: "hidden" })} />
              <ErrorList id={categoryField.weight.errorId} errors={categoryField.weight.errors} />
            </div>
          )}
          <CollapsibleContent>
            <Card className="bg-surface-3 border-line-2 border-l-2 border-l-you/60 mt-4">
              <CardTitle className="px-4 uppercase text-ink-muted">What this pays for</CardTitle>
              {isNetInterest && (
                <CardContent className="text-ink-2 italic">
                  Mandatory obligation — this is not a priority you set. It reflects the cost of
                  existing national debt and cannot be redirected.
                </CardContent>
              )}
              <CardContent className="text-ink-2">{fnData.description}</CardContent>
              {fnData.commonUses && fnData.commonUses.length > 0 && (
                <CardContent className="text-ink-2">
                  <ul>
                    {fnData.commonUses.map((use, i) => (
                      <li key={i} className="ml-4 list-disc">
                        {use}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              )}
            </Card>
          </CollapsibleContent>
        </Collapsible>
      </article>
    );
  };

  return (
    <section ref={contentRef}>
      <div className="mb-4">
        <TypographyH1 className="mb-3">Your turn.</TypographyH1>
        <TypographyLead className="mb-8">
          Slide each category (budget function) to reflect your priorities. Each one is independent
          and does not represent a percentage.
        </TypographyLead>
        <TypographyP>
          Keep in mind:{" "}
          <span className="text-muted-foreground">
            if everything's a priority, nothing is. And you can always change your mind.
          </span>
        </TypographyP>
      </div>
      <form className="flex flex-col gap-4" method="post" {...getFormProps(form)}>
        <HoneypotInputs />
        <div ref={formErrorRef}>
          {form.errors?.length ? (
            <div
              id={form.errorId}
              role="alert"
              className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-foreground-destructive"
            >
              <Info size={18} className="shrink-0" />
              <span>{form.errors.join(" ")}</span>
            </div>
          ) : null}
        </div>
        {/* Progress bar + grouping toggle */}
        <div className="sticky top-(--header-height) bg-background z-10 pt-2" ref={toolbarRef}>
          <div className="flex gap-4 items-center">
            <Progress value={progress * 100} className="grow" />
            <ViewToggle value={viewScheme} onChange={setViewScheme} />
          </div>
        </div>
        <TypographyP className="flex items-center gap-2 text-sm pb-2 text-ink-faint sticky top-[calc(var(--header-height)+var(--priorities-toolbar-height))] bg-background z-10">
          {!existingParticipant && <Info size={18} />}
          <span>
            Tap a{" "}
            <span className="text-ink-2 underline decoration-dotted decoration-you underline-offset-2">
              Budget Function
            </span>{" "}
            for context.
          </span>
        </TypographyP>
        {/* Allocations */}
        <div className="mx-0.5">
          {viewScheme === "flat" ? (
            <AllocationCard>
              {allocations.map((a) => {
                const fnId = a.getFieldset().id.initialValue;
                return fnId ? renderAllocationItem(fnId, a) : null;
              })}
              {renderAllocationItem("net_interest")}
            </AllocationCard>
          ) : (
            <div className="flex flex-col gap-8">
              {PUBLIC_DOMAIN_SCHEME.groups.map((group) => (
                <div key={group.id}>
                  <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold tracking-wide uppercase">
                    <span
                      className="size-3 shrink-0 rounded-sm"
                      style={{ backgroundColor: `var(${group.color.replace(/^text-/, "--")})` }}
                    />
                    {group.label}
                  </h3>
                  <AllocationCard>
                    {group.functionIds.map((fid) => {
                      const entry = allocationsByFunctionId.get(fid);
                      if (!entry) return null;
                      return renderAllocationItem(fid, entry.field);
                    })}
                  </AllocationCard>
                </div>
              ))}
              <AllocationCard>{renderAllocationItem("net_interest")}</AllocationCard>
            </div>
          )}
        </div>
        <div ref={sentinelRef} />
        <BackToTop />
        <Separator className="mt-8 mb-4" />
        <div
          className={cn(
            "flex flex-col bg-background pt-4",
            existingParticipant ? "sticky bottom-0" : "",
          )}
        >
          {existingParticipant ? (
            <>
              <TypographyH2 className="mb-0">Fine-tuning?</TypographyH2>
              <TypographyLead className="mb-12">
                Adjust anything. Your updated priorities will replace the previous comparison.
              </TypographyLead>
            </>
          ) : (
            <>
              <TypographyH2 className="mb-0">How's that feel?</TypographyH2>
              <TypographyLead className="mb-12">
                Now that you've set your priorities, they'll be mapped against Washington's. See
                where you agree, and where you don't. Some of it might surprise you.
              </TypographyLead>
            </>
          )}
          <Button
            type="submit"
            className="h-10 border-ink border-2 bg-surface font-semibold mx-auto py-6 px-7 mb-4"
            variant="outline"
          >
            {existingAllocation ? "Update your comparison" : "See how your priorities compare"}
          </Button>
        </div>
      </form>
    </section>
  );
}

function AllocationCard({ children }: React.PropsWithChildren) {
  return <Card className="bg-surface-2 border p-6">{children}</Card>;
}
